/**
 * LiveGiftModal
 *
 * Lets a watcher send a "live gift" (classic Stellar payment) to the
 * broadcaster while a live session is active.  The modal:
 *
 *   1. Loads both accounts' trustlines from Horizon.
 *   2. Computes the *intersection* — assets BOTH parties can send/
 *      receive without the recipient having to add a trustline first.
 *      XLM (native) is always included because every funded G…
 *      account holds it.
 *   3. Presents the viewer with an asset picker, an amount field
 *      (with the available balance), and an optional short note.
 *   4. On "Send", signs a classic Horizon payment with the viewer's
 *      wallet and submits it.  After Horizon returns `ledger`, it
 *      returns the submission result to the caller so they can
 *      emit the decorative `live_gift` WebSocket event via the
 *      broadcast controller's `sendGift` method.
 *
 * Smart accounts (C…) cannot hold classic trustlines.  If either
 * party is a C-address we surface an explanatory note and disable
 * the form; gifting to / from a smart account would require Soroban
 * token contract invocations which are out of scope for the MVP.
 *
 * The modal intentionally avoids any of TrustlinesPanel's "add / pay /
 * remove" dialogs — a live session is the wrong moment to negotiate
 * new trustlines; the viewer should only be able to send assets they
 * already hold AND which the broadcaster already accepts.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Horizon,
  Asset,
  Networks,
  Operation,
  TransactionBuilder,
  BASE_FEE,
  Memo,
} from '@stellar/stellar-sdk';
import type { TrustlinesWallet, TrustlineRow } from './TrustlinesPanel';

export interface LiveGiftModalProps {
  /** G-address of the broadcaster — recipient of the payment. */
  broadcasterAddress: string;
  /** G-address of the viewer (the sender).  Required for the
   *  intersection check; also shown on the confirmation row. */
  viewerAddress: string;
  /** Signer used to produce the payment transaction.  Must be able
   *  to sign for `viewerAddress`.  (Typically the viewer's
   *  Freighter / WalletConnect wallet wrapper.) */
  viewerWallet: TrustlinesWallet;
  /** Horizon URL override; defaults to TESTNET / PUBLIC based on `network`. */
  horizonUrl?: string;
  /** Stellar network passphrase.  Defaults to TESTNET. */
  network?: Networks;
  /** Close the modal (Cancel / X button / successful send + Ack). */
  onClose: () => void;
  /** Fires once Horizon accepts the tip.  The caller should emit the
   *  decorative `live_gift` WS message so other peers see the toast. */
  onSent: (info: {
    assetCode: string;
    assetIssuer: string | null;
    amount: string;
    memo: string;
    txHash: string;
  }) => void;
}

type SharedAsset = {
  /** Canonical key — `native` or `CODE:ISSUER`. */
  key: string;
  /** Display label — `XLM` or the code. */
  code: string;
  /** Issuer G-address or `null` for native. */
  issuer: string | null;
  /** Available (= balance − selling liabilities) on the viewer's side. */
  viewerAvailable: number;
  /** The Stellar-SDK Asset object for TransactionBuilder. */
  asset: Asset;
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function resolveHorizon(
  horizonUrl?: string,
  network?: Networks,
): { server: Horizon.Server; net: Networks } {
  const net = network ?? Networks.TESTNET;
  const url = horizonUrl
    ?? (net === Networks.PUBLIC ? 'https://horizon.stellar.org' : 'https://horizon-testnet.stellar.org');
  return { server: new Horizon.Server(url), net };
}

function isSmartAccount(addr: string): boolean {
  return /^C[A-Z0-9]{55}$/.test(addr);
}

function shortAddr(addr: string): string {
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

function fmtAmount(s: string): string {
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { maximumFractionDigits: 7 });
}

function rowToAsset(line: TrustlineRow): Asset {
  if (line.asset_type === 'native') return Asset.native();
  if (!line.asset_code || !line.asset_issuer) throw new Error('bad_trustline_row');
  return new Asset(line.asset_code, line.asset_issuer);
}

function rowKey(line: TrustlineRow): string {
  return line.asset_type === 'native' ? 'native' : `${line.asset_code}:${line.asset_issuer}`;
}

function available(line: TrustlineRow): number {
  const bal = parseFloat(line.balance || '0');
  const sell = parseFloat(line.selling_liabilities || '0');
  return Math.max(0, bal - sell);
}

// ── Component ───────────────────────────────────────────────────────────────

export const LiveGiftModal: React.FC<LiveGiftModalProps> = ({
  broadcasterAddress,
  viewerAddress,
  viewerWallet,
  horizonUrl,
  network,
  onClose,
  onSent,
}) => {
  const { server, net } = useMemo(() => resolveHorizon(horizonUrl, network), [horizonUrl, network]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sharedAssets, setSharedAssets] = useState<SharedAsset[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('native');
  const [amount, setAmount] = useState<string>('1');
  const [memo, setMemo] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Smart accounts (C…) can't hold classic trustlines, so classic
  // payments are impossible in either direction.  Short-circuit with
  // a friendly note rather than exposing a form that's guaranteed
  // to fail at Horizon.
  const bcastIsSmart = isSmartAccount(broadcasterAddress);
  const viewerIsSmart = isSmartAccount(viewerAddress);
  const bothClassic = !bcastIsSmart && !viewerIsSmart;

  useEffect(() => {
    if (!bothClassic) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [viewerAcct, bcastAcct] = await Promise.all([
          server.loadAccount(viewerAddress),
          server.loadAccount(broadcasterAddress),
        ]);
        // Map broadcaster's trustlines by key so we can check
        // "can receive" in O(1).  An unauthorized trustline on the
        // broadcaster's side would cause Horizon to reject the
        // payment; filter those out proactively.
        const bcastKeys = new Set<string>(
          (bcastAcct.balances as any[])
            .filter((b) => b.asset_type === 'native' || b.is_authorized !== false)
            .map((b) => rowKey(b)),
        );
        const shared: SharedAsset[] = [];
        for (const b of (viewerAcct.balances as any[])) {
          const row = b as TrustlineRow;
          if (row.is_authorized === false && row.asset_type !== 'native') continue;
          const key = rowKey(row);
          if (!bcastKeys.has(key)) continue;
          try {
            shared.push({
              key,
              code: row.asset_type === 'native' ? 'XLM' : (row.asset_code || '???'),
              issuer: row.asset_type === 'native' ? null : (row.asset_issuer || null),
              viewerAvailable: available(row),
              asset: rowToAsset(row),
            });
          } catch { /* skip malformed row */ }
        }
        shared.sort((a, b) => {
          if (a.key === 'native') return -1;
          if (b.key === 'native') return 1;
          return b.viewerAvailable - a.viewerAvailable;
        });
        if (cancelled) return;
        setSharedAssets(shared);
        setSelectedKey((prev) => shared.find((s) => s.key === prev)?.key || shared[0]?.key || 'native');
      } catch (err: any) {
        if (cancelled) return;
        if (String(err?.response?.status) === '404') {
          setError('One of the accounts is not funded on Stellar yet.');
        } else {
          setError(err?.message || 'Could not load trustlines.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [server, viewerAddress, broadcasterAddress, bothClassic]);

  const selected = useMemo(
    () => sharedAssets.find((s) => s.key === selectedKey) || null,
    [sharedAssets, selectedKey],
  );

  const handleSend = useCallback(async () => {
    if (!selected) return;
    setError(null);
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    // For native XLM we keep a safety margin so the payment + fee
    // doesn't nuke the viewer's minimum reserve.  Other assets don't
    // have this concern (reserve is XLM-only).
    const headroom = selected.key === 'native' ? 1.5 : 0;
    if (amt + headroom > selected.viewerAvailable) {
      setError(`Amount exceeds available balance (${fmtAmount(String(selected.viewerAvailable))} ${selected.code}).`);
      return;
    }

    setSubmitting(true);
    try {
      const sender = await viewerWallet.getPublicKey();
      if (sender !== viewerAddress) {
        // Defence-in-depth: the wallet public key must match the
        // address we computed the trustline intersection against.
        // If the user switched wallets mid-flow, we bail loudly so
        // we don't accidentally charge the new wallet.
        throw new Error('Wallet changed during send — please close the dialog and retry.');
      }
      const source = await server.loadAccount(sender);
      let builder = new TransactionBuilder(source, { fee: BASE_FEE, networkPassphrase: net })
        .addOperation(Operation.payment({
          destination: broadcasterAddress,
          asset: selected.asset,
          amount: amt.toFixed(7),
        }));
      if (memo.trim()) builder = builder.addMemo(Memo.text(memo.trim().slice(0, 28)));
      const tx = builder.setTimeout(60).build();
      const signedXdr = await viewerWallet.signTransaction(tx.toXDR(), net);
      const signed = TransactionBuilder.fromXDR(signedXdr, net);
      const result: any = await server.submitTransaction(signed as any);
      const txHash: string = result?.hash || result?.id || '';
      onSent({
        assetCode: selected.code,
        assetIssuer: selected.issuer,
        amount: amt.toFixed(7),
        memo: memo.trim(),
        txHash,
      });
      onClose();
    } catch (err: any) {
      // Horizon rejections come back with `response.data.extras.result_codes`;
      // surface the tx/op code when available so the user knows *why*
      // rather than just seeing a generic "submit failed".
      const codes = err?.response?.data?.extras?.result_codes;
      if (codes) {
        setError(`Payment rejected: ${codes.transaction || 'tx_failed'}${codes.operations ? ' / ' + codes.operations.join(',') : ''}`);
      } else {
        setError(err?.message || 'Could not send the gift.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [selected, amount, memo, viewerWallet, server, net, viewerAddress, broadcasterAddress, onSent, onClose]);

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Send a live gift</div>
          <button onClick={onClose} style={btnGhost} aria-label="Close gift modal">✕</button>
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>To</span>
          <span style={valStyle} title={broadcasterAddress}>{shortAddr(broadcasterAddress)}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>From</span>
          <span style={valStyle} title={viewerAddress}>{shortAddr(viewerAddress)}</span>
        </div>

        {!bothClassic && (
          <div style={noteWarnStyle}>
            Live gifts currently require both parties to use a classic
            G-address Stellar wallet.  Support for smart-account
            (C-address) wallets is coming soon.
          </div>
        )}

        {bothClassic && loading && (
          <div style={noteStyle}>Loading shared trustlines…</div>
        )}

        {bothClassic && !loading && sharedAssets.length === 0 && !error && (
          <div style={noteWarnStyle}>
            You don't share any assets with this broadcaster yet.  Add
            a trustline to one of their accepted assets (or just send
            XLM) and try again.
          </div>
        )}

        {bothClassic && !loading && sharedAssets.length > 0 && (
          <>
            <div style={rowStyle}>
              <span style={labelStyle}>Asset</span>
              <select
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                style={selectStyle}
                disabled={submitting}
              >
                {sharedAssets.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.code}
                    {a.issuer ? ` · ${shortAddr(a.issuer)}` : ''}
                    {' — '}
                    {fmtAmount(String(a.viewerAvailable))} available
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Amount</span>
              <input
                type="number"
                min="0"
                step="0.0000001"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={inputStyle}
                disabled={submitting}
              />
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Note</span>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value.slice(0, 28))}
                placeholder="optional — up to 28 chars"
                style={inputStyle}
                disabled={submitting}
              />
            </div>
          </>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        <div style={footerStyle}>
          <button onClick={onClose} style={btnGhost} disabled={submitting}>Cancel</button>
          <button
            onClick={handleSend}
            style={submitting || !selected ? btnPrimaryDisabled : btnPrimary}
            disabled={submitting || !selected || !bothClassic}
          >
            {submitting ? (
              'Sending…'
            ) : (
              <>
                <img
                  src="/images/stellar-xlm.png"
                  alt=""
                  aria-hidden
                  width={14}
                  height={14}
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'text-bottom',
                    marginRight: 6,
                  }}
                />
                Send gift
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10_000, padding: 16,
};

const panelStyle: React.CSSProperties = {
  background: '#ffffff', borderRadius: 14, padding: 18,
  width: 'min(100%, 440px)', boxShadow: '0 20px 40px rgba(15,23,42,0.25)',
  display: 'flex', flexDirection: 'column', gap: 10,
};

const headerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 4,
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  borderRadius: 8, padding: '6px 2px',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12, color: '#475569', minWidth: 60,
};

const valStyle: React.CSSProperties = {
  fontSize: 13, color: '#0f172a', fontWeight: 600, flex: 1, textAlign: 'right',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const inputStyle: React.CSSProperties = {
  flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: 14,
};

const selectStyle: React.CSSProperties = {
  ...inputStyle, background: '#fff',
};

const noteStyle: React.CSSProperties = {
  fontSize: 12, color: '#64748b', background: '#f1f5f9',
  padding: '8px 10px', borderRadius: 8,
};

const noteWarnStyle: React.CSSProperties = {
  ...noteStyle, color: '#92400e', background: '#fef3c7',
};

const errorStyle: React.CSSProperties = {
  fontSize: 12, color: '#b91c1c', background: '#fee2e2',
  padding: '8px 10px', borderRadius: 8,
};

const footerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6,
};

const btnGhost: React.CSSProperties = {
  border: '1px solid #e2e8f0', background: '#fff', color: '#334155',
  padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
};

// Gift send button — yellow-and-white Stellar palette.  Matches the
// gift toast + `btnGift` on ProfileOverlay so the tip surfaces all
// share a consistent "warm Stellar" look instead of leaning on the
// blue/red primaries used elsewhere in the app.
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  border: '1px solid #f59e0b',
  background: 'linear-gradient(135deg, #fde68a 0%, #ffffff 100%)',
  color: '#1f2937',
  padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(234, 179, 8, 0.35)',
};

const btnPrimaryDisabled: React.CSSProperties = {
  ...btnPrimary,
  background: '#e5e7eb',
  color: '#94a3b8',
  border: '1px solid #e5e7eb',
  boxShadow: 'none',
  cursor: 'not-allowed',
};
